import React, { useCallback } from 'react';
import CustomSelect, { Option } from '../../../../custom_components/CustomSelect';
import useTelemetry from '../../telemetry/hooks/useTelemetry';
import { useViewport } from '../../telemetry/hooks/useViewport';
import { useDashboardSettings } from '../../telemetry/hooks/useDashboardSettings';
import { useMenu } from '../../telemetry/hooks/useMenu';
import {
    TelemetryLayout,
    TelemetryPanelWrapper,
    TelemetryCard,
    TelemetryCardHeader,
    TelemetryCardTitle,
    TelemetryCardAlertBadge,
    TelemetryCardVizSelect,
    TelemetryCardActions,
    TelemetryCardBody,
    TelemetryCardChart,
    getTelemetrySubColor,
    getPanelPrimaryParam,
} from '../../../../../CommonComponents/telemetry';
import useWidgetMenu from '../../telemetry/hooks/useWidgetMenu';
import TelemetryLoading from '../../telemetry/components/telemetry-loading';
import TelemetryNotFound from '../../telemetry/components/telemetry-not-found';
import './Telemetry.scss';

const TelemetryPanels: React.FC = () => {
    const {
        dashboards,
        activeDashboard,
        activeLayout,
        setLayout,
        patchWidget,
        activeTelemetryDashboardId,
        switchWorkspace,
        telemetrySeries,
        telemetryStatus,
        isTelemetryDataLoading,
    } = useTelemetry();

    const { timeframe, precision, units } = useDashboardSettings();

    const {
        viewport,
        jumpToLive,
        getChartWrapRef,
        viewportLabel,
        isPaused,
        isLoadingHistory,
        isAtStartBoundary,
    } = useViewport(timeframe);

    const { menu } = useMenu();
    const { openWidgetMenu, handleVizClickMenu } = useWidgetMenu();

    const workspaceOptions: Option[] = dashboards.map((db) => ({
        label: db.name,
        value: db.id,
    }));

    const selectedWorkspaceOption =
        workspaceOptions.find((opt) => opt.value === activeTelemetryDashboardId) ||
        workspaceOptions[0] ||
        { label: '', value: '' };

    const handleWorkspaceChange = useCallback(
        (data: Option) => {
            if (data?.value) {
                switchWorkspace(data.value);
            }
        },
        [switchWorkspace]
    );

    if (telemetryStatus === 'loading') {
        return <TelemetryLoading />;
    }

    if (telemetryStatus === 'not_found' || telemetryStatus === 'error' || !dashboards || dashboards.length === 0 || !activeDashboard) {
        return <TelemetryNotFound />;
    }

    return (
        <div className="telemetry__panel__body__container">
            <div className="telemetry__panel__body__container__header">
                <CustomSelect
                    className="header__select__container w-100 rounded-2"
                    options={workspaceOptions}
                    disabled_search={true}
                    value={selectedWorkspaceOption}
                    onChange={handleWorkspaceChange}
                />
            </div>
            <div className="telemetry__panel__body__container__body p-0 pb-4 bg-black">
                <TelemetryLayout
                    layout={activeLayout}
                    onLayoutChange={setLayout}
                    telemetrySeries={telemetrySeries}
                    layoutMode="list"
                    timeframe={timeframe}
                    precision={precision}
                    units={units as any}
                    viewport={viewport as any}
                    viewportLabel={viewportLabel}
                    isPaused={isPaused}
                    isAtStartBoundary={isAtStartBoundary}
                    isLoadingHistory={isLoadingHistory}
                    isTelemetryDataLoading={isTelemetryDataLoading}
                    onJumpLive={jumpToLive}
                    getChartWrapRef={getChartWrapRef}
                    patchWidget={patchWidget}
                    activeMenuTriggerId={menu?.triggerId}
                    style={{ padding: '8px' }}
                >
                    {(layoutCtx) =>
                        activeLayout.map((panel, panelIdx) => {
                            const primaryParam = getPanelPrimaryParam(panel);
                            const subStr = primaryParam.subsystem;
                            const telStr = primaryParam.paramName;
                            const subColor = getTelemetrySubColor(subStr);
                            const numSeries = panel.parameters?.length || 1;
                            const subtitleMeta =
                                (panel.graphMode === 'multi' ||
                                    panel.graphMode === 'cchart' ||
                                    panel.graphMode === 'line' ||
                                    panel.graphMode === 'area') &&
                                    numSeries > 1
                                    ? `${numSeries} series · 1 unit`
                                    : undefined;

                            return (
                                <TelemetryPanelWrapper key={panel.id} panel={panel as any} index={panelIdx}>
                                    <TelemetryCard
                                        panel={panel}
                                        isDrag={layoutCtx.dragId === panel.id}
                                        isResize={layoutCtx.resizeId === panel.id}
                                        onResizeDown={(dir, e) => layoutCtx.onResizeDown(panel.id, dir, e)}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            openWidgetMenu(panel, e, true);
                                        }}
                                    >
                                        <TelemetryCardHeader
                                            panel={panel}
                                            onDragDown={(e) => layoutCtx.onDragDown(panel.id, e)}
                                        >
                                            <TelemetryCardTitle title={panel.name || primaryParam.label} subColor={subColor} subtitle={subtitleMeta}>
                                                {panel.name || primaryParam.label}
                                            </TelemetryCardTitle>
                                            <TelemetryCardAlertBadge panel={panel} />

                                            <TelemetryCardActions>
                                                <TelemetryCardVizSelect
                                                    panel={panel}
                                                    activeMenuTriggerId={menu?.triggerId}
                                                    onVizClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        handleVizClickMenu(panel, e, subStr, telStr)
                                                    }}
                                                />
                                            </TelemetryCardActions>
                                        </TelemetryCardHeader>

                                        <TelemetryCardBody>
                                            <TelemetryCardChart panel={panel} />
                                        </TelemetryCardBody>
                                    </TelemetryCard>
                                </TelemetryPanelWrapper>
                            );
                        })
                    }
                </TelemetryLayout>
            </div>
        </div>
    );
};

export default TelemetryPanels;